import { useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { 
  LayoutDashboard, 
  Users, 
  FileText, 
  Settings, 
  Plus,
  Truck,
  Menu,
  X,
  BarChart3
} from "lucide-react";
import { Button } from "./ui/button";
import { cn } from "../lib/utils";

export default function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navigation = [
    { name: "Dashboard", href: "/", icon: LayoutDashboard },
    { name: "Customers", href: "/customers", icon: Users },
    { name: "Vehicles", href: "/vehicles", icon: Truck },
    { name: "Invoices", href: "/invoices", icon: FileText },
    { name: "Summary", href: "/summary", icon: BarChart3 },
    { name: "Settings", href: "/settings", icon: Settings },
  ];

  const closeSidebar = () => setSidebarOpen(false);

  return (
    <div className="flex min-h-screen bg-slate-50" data-testid="main-layout">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-slate-900 text-white px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-1.5 bg-blue-600 rounded-lg">
            <Truck className="h-5 w-5" />
          </div>
          <span className="font-bold font-['Manrope']">OP Road Service</span>
        </div>
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
          data-testid="mobile-menu-btn"
        >
          {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={closeSidebar}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={cn(
          "no-print fixed lg:static inset-y-0 left-0 z-50 w-64 bg-slate-900 text-white flex flex-col transform transition-transform duration-300 ease-in-out",
          "lg:transform-none",
          sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        )}
        data-testid="sidebar"
      >
        {/* Logo */}
        <div className="p-6 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-600 rounded-lg">
              <Truck className="h-6 w-6" />
            </div>
            <div>
              <h1 className="font-bold text-lg font-['Manrope'] tracking-tight">OP Road Service</h1>
              <p className="text-xs text-slate-400">Invoice System</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {navigation.map((item) => (
            <NavLink
              key={item.name}
              to={item.href}
              end={item.href === "/"}
              onClick={closeSidebar}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200",
                  isActive
                    ? "bg-blue-600 text-white shadow-lg shadow-blue-600/30"
                    : "text-slate-300 hover:bg-slate-800 hover:text-white"
                )
              }
              data-testid={`nav-${item.name.toLowerCase()}`}
            >
              <item.icon className="h-5 w-5" />
              <span className="font-medium">{item.name}</span>
            </NavLink>
          ))}
        </nav>

        {/* Quick Actions */}
        <div className="p-4 border-t border-slate-800">
          <NavLink to="/invoices/new" onClick={closeSidebar}>
            <Button 
              className="w-full bg-blue-600 hover:bg-blue-700 text-white gap-2 btn-animate"
              data-testid="new-invoice-btn"
            >
              <Plus className="h-4 w-4" />
              New Invoice
            </Button>
          </NavLink>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto lg:ml-0 pt-14 lg:pt-0">
        <Outlet />
      </main>
    </div>
  );
}
